import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";

const STYLE_ID = "leo-preloader-styles";

function injectStylesOnce() {
  if (document.getElementById(STYLE_ID)) return;
  const css = `
    /* ---- hard overrides (no collisions) ---- */
    html.leo-prelock, body.leo-prelock { overflow: hidden !important; }

    #leo-preloader-overlay {
      position: fixed !important;
      inset: 0 !important;
      width: 100vw !important;
      height: 100dvh !important; /* mobile-safe */
      min-height: 100vh !important;
      z-index: 2147483647 !important;
      display: grid !important;
      place-items: center !important;
      background: #07122A !important; /* solid dark navy */
      isolation: isolate !important;
      opacity: 1 !important;
    }
    #leo-preloader-overlay::before {
      content: "" !important;
      position: absolute !important;
      inset: 0 !important;
      background:
        radial-gradient(1200px 600px at 20% -10%, rgba(63,132,255,0.10), transparent 60%),
        radial-gradient(1000px 500px at 100% 10%, rgba(0,173,255,0.08), transparent 55%) !important;
      pointer-events: none !important;
    }
    #leo-preloader-overlay.leo-hide {
      animation: leoFadeOut .28s ease forwards !important;
    }
    @keyframes leoFadeOut { to { opacity: 0; visibility: hidden; } }

    
    .leo-pre__glow {
      position: absolute !important;
      inset: -32px !important;
      background: radial-gradient(280px 140px at 0% 0%, rgba(99,102,241,.18), transparent 60%) !important;
      pointer-events: none !important;
    }
    .leo-pre__row {
      position: relative !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      gap: 8px !important;
    }
    .leo-pre__logo {
      height: 28px !important;
      width: 28px !important;
      border-radius: 6px !important;
      object-fit: contain !important;
      user-select: none !important;
      animation: slideInL .6s ease-out both !important;
    }
    .leo-pre__title {
      font-size: 13px !important;
      font-weight: 600 !important;
      white-space: nowrap !important;
      color: rgba(255,255,255,0.92) !important;
      letter-spacing: .2px !important;
      animation: slideInR .6s ease-out both !important;
    }
    @keyframes slideInL {
      0%   { transform: translateX(-28px); opacity: 0; }
      60%  { transform: translateX(6px);  opacity: 1; }
      100% { transform: translateX(0);    opacity: 1; }
    }
    @keyframes slideInR {
      0%   { transform: translateX(28px); opacity: 0; }
      60%  { transform: translateX(-6px); opacity: 1; }
      100% { transform: translateX(0);    opacity: 1; }
    }

    .leo-pre__tag {
      margin-top: 6px !important;
      text-align: center !important;
      font-size: 9px !important;
      line-height: 1 !important;
      color: rgba(255,255,255,0.55) !important;
      letter-spacing: 0.08em !important;
      user-select: none !important;
    }
    .leo-pre__tag b { color: rgba(255,255,255,0.72) !important; font-weight: 600 !important; }

    /* Respect reduced motion */
    @media (prefers-reduced-motion: reduce) {
      .leo-pre__logo, .leo-pre__title { animation: none !important; opacity: 1 !important; transform: none !important; }
      #leo-preloader-overlay.leo-hide { animation-duration: .01ms !important; }
    }
  `;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.appendChild(document.createTextNode(css));
  document.head.appendChild(style);
}

export default function PreloaderOverlay({ show, duration = 1200, onDone }) {
  const [visible, setVisible] = useState(!!show);

  useEffect(() => {
    injectStylesOnce();
  }, []);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;

    if (show) {
      setVisible(true);
      html.classList.add("leo-prelock");
      body.classList.add("leo-prelock");

      const id = setTimeout(() => {
        setVisible(false);
        html.classList.remove("leo-prelock");
        body.classList.remove("leo-prelock");
        onDone?.();
      }, duration);

      return () => {
        clearTimeout(id);
        html.classList.remove("leo-prelock");
        body.classList.remove("leo-prelock");
      };
    } else {
      setVisible(false);
      html.classList.remove("leo-prelock");
      body.classList.remove("leo-prelock");
    }
  }, [show, duration, onDone]);

  if (!visible && !show) return null;

  const overlay = (
    <div id="leo-preloader-overlay" className={!show ? "leo-hide" : ""}>
      <div className="leo-pre__tile">
        <div className="leo-pre__glow" aria-hidden="true" />
        <div className="leo-pre__row">
          <img
            className="leo-pre__logo"
            src="/club.png"
            alt="Logo"
            onError={(e) => (e.currentTarget.src = "/default-logo.png")}
            draggable="false"
          />
          <span className="leo-pre__title">Leo Club of SLIIT</span>
        </div>

        <div className="leo-pre__tag">
          Powered by <b>SLIIT Leos</b>
        </div>
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
}
